import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContextualMenu } from '../../components/menus/ContextualMenu';
import * as routing from '../../constants/Routing';
import { ConfirmDeleteDialog } from '../../dialogs/ConfirmDeleteDialog';
import { ExportStopsReportsDialog } from '../../dialogs/ExportStopsReportsDialog';
import { ExportStopsTasksDialog } from '../../dialogs/ExportStopsTasksDialog';
import { MoveStopsDialog } from '../../dialogs/MoveStopsDialog';
import { StopsToSupervisorDialog } from '../../dialogs/StopsToSupervisorDialog';
import { StopFlat } from '../../domain/stop/StopFlat';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useDeleteStops } from '../../hooks/stop/useDeleteStops';
import { useSaveStopsAsClients } from '../../hooks/stop/useSaveStopsAsClients';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Text from '../../react-components/Text';
import eventsPanel from '../../services/events';
import { MixPanelEvents } from '../../services/mixpanel';
import { useSetSelectedStops } from './hooks/useSetSelectedStops';

type StopsActionsMenuType = `contextual` | `popover`;

type StopsContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type StopsActionsMenuProps = {
  type?: StopsActionsMenuType;
  stops: Array<StopFlat>;
  planId?: string;
  contextAnchor?: StopsContextAnchor;
  showViewDetails?: boolean;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  setContextAnchor?: (context?: StopsContextAnchor) => void;
  afterDeleteStops?: (stopsIds: string[]) => void;
};

export const StopsActionsMenu = ({
  type = `contextual` as StopsActionsMenuType,
  stops,
  planId,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  showViewDetails = true,
  popoverProps,
  setContextAnchor = () => {},
  afterDeleteStops,
}: StopsActionsMenuProps) => {
  const navigate = useNavigate();

  const { selectedStops, setSelectedStops } = useSetSelectedStops();

  const { data: activeProject } = useRetrieveActiveProject();

  const { data: userCredentials } = useRetrieveUserCredentials();

  const { setRightPanelInfo } = useRetrieveRightPanelInfo();

  const { run: deleteStops, success: deleteStopsSuccess, isLoading: deleteStopsLoading } = useDeleteStops({ planId });

  const { run: saveStopsAsClient } = useSaveStopsAsClients();

  const [dialog, setDialog] = useState<
    `deleteStops` | `moveStops` | `exportReports` | `exportTasks` | `assignToSupervisor` | undefined
  >(undefined);

  const hasCustomersPermissions = userCredentials?.subscription.test(`customers`);
  const hasSupervisorsPermissions = userCredentials?.subscription.test(`supervisors`);

  useEffect(() => {
    if (deleteStopsSuccess) {
      setSelectedStops(
        selectedStops.filter((selectedStopsId) => !stops.map((stop) => stop.id).includes(selectedStopsId))
      );

      if (afterDeleteStops) {
        afterDeleteStops(stops.map((stop) => stop.id));
      }

      setDialog(undefined);
      if (setContextAnchor) {
        setContextAnchor(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStopsSuccess]);
  if (stops.length === 0 && !deleteStopsLoading) return null;
  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="stops-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,

            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showViewDetails || stops.length !== 1
                ? []
                : [
                    {
                      key: `view_details`,
                      width: 240,
                      onClick: () => {
                        setRightPanelInfo({
                          detailType: `stop`,
                          detailId: `${stops[0].id}`,
                          detailTab: null,
                        });
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.view_details);
                      },
                      content: <Text intlId="services.actions.view_details" style={{ cursor: `pointer` }} />,
                    },
                    null,
                  ]),
              ...(!activeProject || stops.length !== 1 || !stops[0].plan_id
                ? []
                : [
                    {
                      key: `open_in_plan`,
                      width: 240,
                      onClick: () => {
                        navigate(
                          `${routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(
                            `:projectId`,
                            `${activeProject!.id}`
                          ).replace(`:planId`, `${stops[0].plan_id}`)}?detailId=${stops[0].id}&detailType=stop`
                        );
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.open_in_plan);
                      },
                      content: <Text intlId="services.actions.open_in_plan" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              {
                key: `save_as_client`,
                width: 240,
                disabled: stops.some((s) => Boolean(s.client_id)),
                onClick: () => {
                  saveStopsAsClient({ stopIds: stops.map((stop) => stop.id) });
                  eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.save_as_client);
                },
                content: <Text intlId="services.actions.save_as_client" style={{ cursor: `pointer` }} />,
              },
              {
                key: `move_stops_to_plan`,
                width: 240,
                onClick: () => {
                  setDialog(`moveStops`);
                  eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.move_to_plan);
                },
                content: <Text intlId="services.actions.move_stops_to_plan" style={{ cursor: `pointer` }} />,
              },
              null,
              {
                key: `export_reports`,
                width: 240,
                onClick: () => {
                  setDialog(`exportReports`);
                  eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.export_reports);
                },
                content: <Text intlId="services.actions.export_reports" style={{ cursor: `pointer` }} />,
              },
              ...(!activeProject?.tasks_enabled
                ? []
                : [
                    {
                      key: `export_tasks`,
                      width: 240,
                      onClick: () => {
                        setDialog(`exportTasks`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.export_tasks);
                      },
                      content: <Text intlId="services.actions.export_tasks" style={{ cursor: `pointer` }} />,
                    },
                  ]),

              ...((hasCustomersPermissions && stops.length === 1) || hasSupervisorsPermissions ? [null] : []),

              ...(!hasCustomersPermissions || stops.length !== 1
                ? []
                : [
                    {
                      key: `open_in_customers`,
                      width: 240,
                      onClick: () => {
                        if (activeProject) {
                          window.open(`${activeProject.getCustomersDomain()}/stop/${stops[0].id}`, `_blank`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.open_in_customers);
                        }
                      },
                      content: <Text intlId="services.actions.open_in_customers" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!hasSupervisorsPermissions
                ? []
                : [
                    {
                      key: `assign_stops_to_supervisor`,
                      width: 240,
                      onClick: () => {
                        setDialog(`assignToSupervisor`);
                        eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.assign_to_supervisor);
                      },
                      content: (
                        <Text intlId="services.actions.assign_stops_to_supervisor" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              null,
              {
                key: `delete_service`,
                width: 240,
                onClick: () => {
                  setDialog(`deleteStops`);
                  eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.delete);
                },
                content: (
                  <Text
                    intlId="services.actions.delete_service"
                    style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                  />
                ),
              },
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}

      {type === `contextual` && (
        <>
          {contextAnchor && (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
              options={[
                ...(showViewDetails && stops.length === 1
                  ? [
                      {
                        key: `view_details`,
                        text: <Text intlId="services.actions.view_details" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          setRightPanelInfo({
                            detailType: `stop`,
                            detailId: `${stops[0].id}`,
                            detailTab: null,
                          });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.view_details);
                        },
                      },
                      null,
                    ]
                  : []),
                ...(activeProject && stops.length === 1 && !!stops[0].plan_id
                  ? [
                      {
                        key: `open_in_plan`,
                        text: <Text intlId="services.actions.open_in_plan" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          navigate(
                            `${routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(
                              `:projectId`,
                              `${activeProject!.id}`
                            ).replace(`:planId`, `${stops[0].plan_id}`)}?detailId=${stops[0].id}&detailType=stop`
                          );

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.open_in_plan);
                        },
                      },
                    ]
                  : []),
                {
                  key: `save_as_client`,
                  disabled: stops.some((s) => Boolean(s.client_id)),
                  text: <Text intlId="services.actions.save_as_client" style={{ cursor: `pointer` }} />,
                  onClick: (event: any) => {
                    event.stopPropagation();
                    saveStopsAsClient({ stopIds: stops.map((stop) => stop.id) });

                    if (setContextAnchor) {
                      setContextAnchor(undefined);
                    }
                    eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.save_as_client);
                  },
                },
                {
                  key: `move_stops_to_plan`,
                  text: <Text intlId="services.actions.move_stops_to_plan" style={{ cursor: `pointer` }} />,
                  onClick: (event: any) => {
                    event.stopPropagation();
                    setDialog(`moveStops`);
                    eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.move_to_plan);
                  },
                },
                null,
                {
                  key: `export_reports`,
                  text: <Text intlId="services.actions.export_reports" style={{ cursor: `pointer` }} />,
                  onClick: (event: any) => {
                    event.stopPropagation();
                    setDialog(`exportReports`);
                    eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.export_reports);
                  },
                },
                ...(activeProject?.tasks_enabled
                  ? [
                      {
                        key: `export_tasks`,
                        text: <Text intlId="services.actions.export_tasks" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`exportTasks`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.export_tasks);
                        },
                      },
                    ]
                  : []),

                ...((hasCustomersPermissions && stops.length === 1) || hasSupervisorsPermissions ? [null] : []),

                ...(hasCustomersPermissions && stops.length === 1
                  ? [
                      {
                        key: `open_in_customers`,
                        text: <Text intlId="services.actions.open_in_customers" style={{ cursor: `pointer` }} />,
                        onClick: () => {
                          if (activeProject) {
                            window.open(`${activeProject.getCustomersDomain()}/stop/${stops[0].id}`, `_blank`);
                            eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.open_in_customers);
                          }
                        },
                      },
                    ]
                  : []),
                ...(hasSupervisorsPermissions
                  ? [
                      {
                        key: `assign_stops_to_supervisor`,
                        text: (
                          <Text intlId="services.actions.assign_stops_to_supervisor" style={{ cursor: `pointer` }} />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`assignToSupervisor`);
                          eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.assign_to_supervisor);
                        },
                      },
                    ]
                  : []),
                null,
                {
                  key: `delete_service`,
                  text: (
                    <Text
                      intlId="services.actions.delete_service"
                      style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                    />
                  ),
                  onClick: (event: any) => {
                    event.stopPropagation();
                    setDialog(`deleteStops`);
                    eventsPanel.stopActionsMenu(MixPanelEvents.stop.actions_menu.delete);
                  },
                },
              ]}
            />
          )}
        </>
      )}

      <ConfirmDeleteDialog
        typeData="services"
        open={dialog === `deleteStops` || deleteStopsLoading}
        loading={deleteStopsLoading}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        data={stops}
        handleDelete={() => {
          deleteStops({ stopIds: stops.map((s) => s.id) });
        }}
      />

      <MoveStopsDialog
        open={dialog === `moveStops`}
        selectedStopIds={stops.map((stop) => stop.id)}
        onCloseDialog={(e, moved, shouldNavigate) => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          if (moved) {
            setSelectedStops([]);
          }
        }}
      />

      <StopsToSupervisorDialog
        open={dialog === `assignToSupervisor`}
        stopIds={stops.map((stop) => stop.id)}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
      />

      {activeProject && (
        <ExportStopsReportsDialog
          open={dialog === `exportReports`}
          selectedStopIds={stops.map((stop) => stop.id)}
          onCloseDialog={() => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
        />
      )}

      {activeProject && (
        <ExportStopsTasksDialog
          open={dialog === `exportTasks`}
          selectedStopIds={stops.map((stop) => stop.id)}
          onCloseDialog={() => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
        />
      )}
    </>
  );
};
